import React, { useState, useEffect, useMemo } from "react";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import {
  PrimaryButton,
  SelectDropdown,
  SelectDropdownOption,
  AppThemeProvider
} from "@cadmakers-ui/core";
import { Trigger } from "../../Models/Trigger";
import styles from "../../Styles/WorkflowTriggerSelector.module.scss";
import { WorkflowSelectorResultsBox } from "./WorkflowSelectorResultsBox";

type Props = {
  selectedTriggers: Trigger[];
  availableTriggers: Trigger[];
  disabled?: boolean;
  onChange: (selectedTriggers: Trigger[]) => void;
};

export const WorkflowTriggerSelector: React.FC<Props> = props => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dropdownSelection, setDropdownSelection] = useState<SelectDropdownOption | undefined>();

  const memoDropdownOptions = useMemo(() => {
    const selectedTriggerCodes = props.selectedTriggers.map(selected => selected.code);
    return props.availableTriggers
      .filter(t => !selectedTriggerCodes.includes(t.code))
      .map(trigger => {
        return { value: trigger.code, label: trigger.name };
      });
  }, [props.selectedTriggers]);

  useEffect(() => {
    if (memoDropdownOptions.length > 0) {
      setDropdownSelection(memoDropdownOptions[0]);
    } else {
      setDropdownSelection(undefined);
    }
  }, [memoDropdownOptions]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };

  const clickRemoveTriggerInResultsBox = (trigger: Trigger) => {
    props.onChange(props.selectedTriggers.filter(t => t.code !== trigger.code));
  };

  const onSaveChanges = () => {
    if (
      dropdownSelection !== undefined &&
      !props.selectedTriggers.map(t => t.code).includes(dropdownSelection.value as number)
    ) {
      props.onChange(
        props.selectedTriggers.concat([
          {
            code: dropdownSelection.value as number,
            name: dropdownSelection.label as string,
            id: props.availableTriggers.find(t => t.code === (dropdownSelection.value as number))!
              .id
          }
        ])
      );
    }
    toggleModal();
  };

  return (
    <>
      <WorkflowSelectorResultsBox
        selectedItems={props.selectedTriggers}
        addButtonTitle="Add Trigger"
        removeButtonTitle="Remove Trigger"
        onClickAdd={toggleModal}
        onClickRemove={clickRemoveTriggerInResultsBox}
        disableAddButton={memoDropdownOptions.length === 0}
      />
      <AppThemeProvider app="cmKnowledge">
        <Modal isOpen={isModalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>Add Automatic Triggers</ModalHeader>
          <ModalBody>
            <div>
              <div className={styles.dropdownContainer}>
                <span style={{ paddingRight: "20px" }}>Trigger</span>
                <span style={{ width: "100%" }}>
                  <SelectDropdown
                    options={memoDropdownOptions}
                    value={dropdownSelection}
                    setDropdownPortalToBody
                    onChange={newValue => setDropdownSelection(newValue as SelectDropdownOption)}
                  />
                </span>
              </div>
              <div className={styles.buttonContainer}>
                <PrimaryButton
                  application="cmKnowledge"
                  type="confirm"
                  onClick={onSaveChanges}
                  customStyle={{ marginRight: "30px" }}
                />
                <PrimaryButton
                  application="cmFeedback"
                  neutral
                  type="cancel"
                  onClick={toggleModal}
                />
              </div>
            </div>
          </ModalBody>
        </Modal>
      </AppThemeProvider>
    </>
  );
};
